import { MediaMatcher } from '@angular/cdk/layout';

import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    inject,
    Input,
    OnDestroy,
    OnInit,
    ViewChild,
} from '@angular/core';
import { MatSidenav } from '@angular/material/sidenav';
import { RouterModule } from '@angular/router';
import { Banner, BannerComponent } from '@cargos/sprintpay-ui';
import { map, Subject, takeUntil } from 'rxjs';
import { InitialFacilityConfig } from '../../models/config/facility-config';
import { ConfigService, SessionService } from '../../services';
import { BannersService } from '../../services/banners.service';
import { FeatureRestrictionsService } from '../../services/features/feature-restrictions.service';
import { HeaderConfigService } from '../../services/header-config.service';
import { HeaderItemComponent } from '../redirect/redirect.component';
import { CartComponent } from './cart/cart.component';
import { HeaderLoginComponent } from './header-login/header-login.component';
import { HeaderLogoComponent } from './header-logo/header-logo.component';
import { HeaderMaterialModule } from './header-material-modules.module';
import { SidenavService } from './services/side-nav.service';
import { SettingsComponent } from './settings/settings.component';

@Component({
    standalone: true,
    selector: 'app-sprintpay-header',
    templateUrl: './header.component.html',
    styleUrls: ['./header.component.scss'],
    imports: [
        SettingsComponent,
        RouterModule,
        CartComponent,
        HeaderMaterialModule,
        HeaderItemComponent,
        HeaderLogoComponent,
        HeaderLoginComponent,
        BannerComponent,
    ],
    providers: [SidenavService],
})
export class HeaderComponent implements OnInit, OnDestroy, AfterViewInit {
    private unsubscribe$ = new Subject<void>();
    private _mobileQueryListener: () => void;
    private readonly sessionService = inject(SessionService);
    public isAuthenticated = false;
    public mobileQuery: MediaQueryList;
    public smallQuery: MediaQueryList;
    public mediumQuery: MediaQueryList;
    public isHistoryEnable = false;
    public isAutoPaySubscriptionEnable = false;
    public isFAQEnable = false;
    public faqConfig: InitialFacilityConfig['faqExternalURL'] = {};
    public touBanner: Banner | null = null;
    public banners: Banner[] = [];
    @Input() settingsButtonOutline: boolean = false;

    @ViewChild('snav') public sidenav: MatSidenav;

    constructor(
        private changeDetectorRef: ChangeDetectorRef,
        private media: MediaMatcher,
        private sidenavService: SidenavService,
        private configService: ConfigService,
        private headerConfigService: HeaderConfigService,
        private bannersService: BannersService,
        private featureRestrictionsService: FeatureRestrictionsService
    ) {
        // TODO: Consider using Bootstrap media queries instead of Angular CDK layout for better responsiveness.
        this.mobileQuery = this.media.matchMedia('(max-width: 700px)');
        this.smallQuery = this.media.matchMedia('(max-width: 950px)');
        this.mediumQuery = this.media.matchMedia('(max-width: 1250px)');
        this._mobileQueryListener = () => this.changeDetectorRef.detectChanges();
    }

    @Input() showFacilityInformation = false;
    @Input() facilityInformationURL: string;
    @Input() homeURL: string;
    @Input() administrativeLoginURL: string;
    @Input() cartAllowedRoutes: string[] = [];
    @Input() cartDisallowedRoutes: string[] = [];
    @Input() loginDisallowedRoutes: string[] = [];
    @Input() widthLogo: string = '145px';
    @Input() heightLogo: string = '45px';
    @Input() homeIconOutlined: boolean = false;
    @Input() showSettingsAtTheEnd: boolean = false;

    ngOnInit(): void {
        this.subscribeMobileSize();
        this.subscribeConfig();
        this.getFaqConfig();
        this.subscribeTOUBanner();
        this.changeDetectorRef.detectChanges();
        this.subscribeBanners();
    }

    ngOnDestroy(): void {
        this.mobileQuery.removeEventListener('change', this._mobileQueryListener);

        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    ngAfterViewInit(): void {
        this.sidenavService.setSidenav(this.sidenav);
    }

    subscribeMobileSize(): void {
        this.mobileQuery.addEventListener('change', this._mobileQueryListener);
    }

    subscribeConfig(): void {
        this.sessionService
            .isAuthenticated$()
            .pipe(
                map((isAuthenticated) => {
                    return [
                        isAuthenticated && this.configService.isPaymentHistoryEnable(),
                        isAuthenticated && this.configService.isAutoPaySubscriptionEnable(),
                    ];
                }),
                takeUntil(this.unsubscribe$)
            )
            .subscribe(([isHistoryEnable, isAutoPaySubscriptionEnable]) => {
                this.isHistoryEnable = isHistoryEnable;
                this.isAutoPaySubscriptionEnable = isAutoPaySubscriptionEnable;
            });
    }

    get headerVisible(): boolean {
        return this.headerConfigService.getVisibilityValue();
    }

    getFaqConfig(): void {
        const configFAQ = this.featureRestrictionsService.isFAQAvailable();
        this.isFAQEnable = configFAQ.isEnabled;
        this.faqConfig = {
            externalURL: configFAQ.externalURL,
            pdfURL: configFAQ.pdfURL,
        };
    }

    subscribeTOUBanner(): void {
        this.bannersService
            .getTOUBanner()
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe((banner) => {
                this.touBanner = banner;
                this.changeDetectorRef.detectChanges();
            });
    }

    private subscribeBanners(): void {
        this.bannersService
            .getBanners()
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe({
                next: (banners) => {
                    this.banners = banners;
                },
            });
    }
}
